import React, { useCallback, useEffect, useState } from 'react';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import FingerprintOutlinedIcon from '@mui/icons-material/FingerprintOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import AddIcon from '@mui/icons-material/Add';
import {
  startRegistration,
  browserSupportsWebAuthn,
} from '@simplewebauthn/browser';
import api from '../services/api';
import StatusChip from './ui/StatusChip';

interface PasskeyItem {
  id: string;
  label: string | null;
  createdAt: string;
  lastUsedAt: string | null;
}

const SectionHead: React.FC<{ icon: React.ReactNode; title: string; subtitle?: string }> = ({
  icon,
  title,
  subtitle,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
    <Box
      sx={{
        width: 38,
        height: 38,
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        borderRadius: 1,
        '& svg': { fontSize: 22 },
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography variant="h6" component="h2" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Box>
  </Box>
);

const SURFACE = { p: { xs: 2, sm: 3 }, border: '1px solid', borderColor: 'divider' } as const;

const SecuritySettings: React.FC = () => {
  // ---- Password change ----
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwSuccess, setPwSuccess] = useState(false);

  const canSubmitPw = current.length >= 1 && next.length >= 6 && next === confirm;

  const submitPassword = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setPwError(null);
    setPwSuccess(false);
    if (next !== confirm) {
      setPwError('A confirmação não corresponde à nova senha.');
      return;
    }
    setPwLoading(true);
    try {
      await api.post('/users/me/change-password', {
        currentPassword: current,
        newPassword: next,
      });
      setPwSuccess(true);
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (err: any) {
      setPwError(err?.response?.data?.message || 'Não foi possível alterar a senha.');
    } finally {
      setPwLoading(false);
    }
  };

  // ---- Passkeys ----
  const supported = browserSupportsWebAuthn();
  const [passkeys, setPasskeys] = useState<PasskeyItem[]>([]);
  const [pkLoading, setPkLoading] = useState(true);
  const [pkBusy, setPkBusy] = useState(false);
  const [pkError, setPkError] = useState<string | null>(null);
  const [pkInfo, setPkInfo] = useState<string | null>(null);

  const loadPasskeys = useCallback(async (): Promise<void> => {
    setPkLoading(true);
    try {
      const { data } = await api.get<PasskeyItem[]>('/auth/passkey/list');
      setPasskeys(data);
    } catch {
      setPasskeys([]);
    } finally {
      setPkLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPasskeys();
  }, [loadPasskeys]);

  const addPasskey = async (): Promise<void> => {
    setPkError(null);
    setPkInfo(null);
    setPkBusy(true);
    try {
      const { data: options } = await api.post('/auth/passkey/register/options');
      const attResp = await startRegistration({ optionsJSON: options });
      const label =
        typeof navigator !== 'undefined' && navigator.platform ? navigator.platform : undefined;
      await api.post('/auth/passkey/register/verify', { response: attResp, label });
      setPkInfo('Passkey adicionada! Agora você pode entrar sem senha.');
      await loadPasskeys();
    } catch (err: any) {
      if (err?.name === 'NotAllowedError') {
        setPkError('Registro cancelado.');
      } else {
        setPkError(err?.response?.data?.message || 'Não foi possível adicionar a passkey.');
      }
    } finally {
      setPkBusy(false);
    }
  };

  const removePasskey = async (id: string): Promise<void> => {
    setPkError(null);
    setPkInfo(null);
    setPkBusy(true);
    try {
      await api.delete(`/auth/passkey/${id}`);
      await loadPasskeys();
    } catch (err: any) {
      setPkError(err?.response?.data?.message || 'Não foi possível remover a passkey.');
    } finally {
      setPkBusy(false);
    }
  };

  const fmtDate = (v?: string | null): string =>
    v ? new Date(v).toLocaleDateString('pt-BR') : '—';

  return (
    <Stack spacing={{ xs: 2, md: 3 }}>
      {/* ---- Change password ---- */}
      <Paper component="form" onSubmit={submitPassword} sx={SURFACE}>
        <SectionHead
          icon={<LockOutlinedIcon />}
          title="Alterar senha"
          subtitle="Informe a senha atual para definir uma nova."
        />
        {pwError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPwError(null)}>
            {pwError}
          </Alert>
        )}
        {pwSuccess && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setPwSuccess(false)}>
            Senha alterada com sucesso.
          </Alert>
        )}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Senha atual"
              type={showCurrent ? 'text' : 'password'}
              fullWidth
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              slotProps={{
                input: {
                  endAdornment: (
                    <IconButton onClick={() => setShowCurrent((v) => !v)} edge="end" aria-label="Mostrar senha atual">
                      {showCurrent ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                    </IconButton>
                  ),
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }} />
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Nova senha"
              type={showNext ? 'text' : 'password'}
              fullWidth
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              helperText="Mínimo de 6 caracteres."
              error={next.length > 0 && next.length < 6}
              slotProps={{
                input: {
                  endAdornment: (
                    <IconButton onClick={() => setShowNext((v) => !v)} edge="end" aria-label="Mostrar nova senha">
                      {showNext ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                    </IconButton>
                  ),
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Confirmar nova senha"
              type={showNext ? 'text' : 'password'}
              fullWidth
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              error={confirm.length > 0 && confirm !== next}
              helperText={confirm.length > 0 && confirm !== next ? 'As senhas não coincidem.' : ' '}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
              <Button
                type="submit"
                variant="contained"
                startIcon={pwLoading ? <CircularProgress size={18} color="inherit" /> : <KeyOutlinedIcon />}
                disabled={!canSubmitPw || pwLoading}
              >
                Alterar senha
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* ---- Passkeys ---- */}
      <Paper sx={SURFACE}>
        <SectionHead
          icon={<FingerprintOutlinedIcon />}
          title="Passkeys"
          subtitle="Entre sem senha usando biometria, PIN ou chave do dispositivo."
        />

        {!supported ? (
          <Alert severity="info">Este navegador/dispositivo não suporta passkeys.</Alert>
        ) : (
          <>
            {pkError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPkError(null)}>
                {pkError}
              </Alert>
            )}
            {pkInfo && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setPkInfo(null)}>
                {pkInfo}
              </Alert>
            )}

            {pkLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : passkeys.length === 0 ? (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Você ainda não tem passkeys. Ao adicionar uma, poderá entrar apenas com o e-mail +
                  passkey; remova todas para voltar a exigir a senha.
                </Typography>
              </Box>
            ) : (
              <Stack divider={<Divider flexItem />} sx={{ mb: 2 }}>
                {passkeys.map((pk) => (
                  <Box
                    key={pk.id}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.25 }}
                  >
                    <FingerprintOutlinedIcon sx={{ color: 'primary.main' }} />
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700 }}>
                        {pk.label || 'Passkey'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Criada em {fmtDate(pk.createdAt)}
                        {pk.lastUsedAt ? ` · usada em ${fmtDate(pk.lastUsedAt)}` : ' · nunca usada'}
                      </Typography>
                    </Box>
                    <Tooltip title="Remover passkey">
                      <span>
                        <IconButton
                          color="error"
                          onClick={() => void removePasskey(pk.id)}
                          disabled={pkBusy}
                          aria-label="Remover passkey"
                        >
                          <DeleteOutlinedIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                ))}
              </Stack>
            )}

            <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={pkBusy ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
                onClick={() => void addPasskey()}
                disabled={pkBusy}
              >
                Adicionar passkey
              </Button>
              {passkeys.length > 0 ? (
                <StatusChip label="Login sem senha ativo" tone="success" />
              ) : null}
            </Stack>
          </>
        )}
      </Paper>
    </Stack>
  );
};

export default SecuritySettings;
